<template>
  <Teleport to="body">
    <Transition name="tour-fade">
      <div v-if="isVisible" class="fixed inset-0 z-[9998]" @click.self="handleOverlayClick">
        <!-- Dark overlay with cutout for spotlight -->
        <svg class="absolute inset-0 w-full h-full pointer-events-auto" xmlns="http://www.w3.org/2000/svg" @click="handleOverlayClick">
          <defs>
            <mask id="spotlight-mask">
              <!-- White background (visible - will show dark overlay) -->
              <rect x="0" y="0" width="100%" height="100%" fill="white"/>
              <!-- Black cutout (transparent - target element stays clear) -->
              <rect
                v-if="targetRect"
                :x="targetRect.left - 8"
                :y="targetRect.top - 8"
                :width="targetRect.width + 16"
                :height="targetRect.height + 16"
                rx="12"
                fill="black"
              />
            </mask>
            <filter id="blur-filter">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3"/>
            </filter>
          </defs>
          <!-- Dark blurred overlay with cutout -->
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 0, 0, 0.65)" mask="url(#spotlight-mask)" filter="url(#blur-filter)"/>
        </svg>

        <!-- Skip hint at top -->
        <div class="absolute top-20 right-2 sm:right-4 pointer-events-auto max-w-[calc(100vw-16px)] sm:max-w-md">
          <div class="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 sm:px-4 shadow-lg border border-gray-200">
            <p class="text-[10px] sm:text-xs text-gray-600">
              <kbd class="px-1.5 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">ESC</kbd>
              <span class="hidden sm:inline"> veya bu alana tıklayarak turu atlayabilirsiniz</span>
              <span class="inline sm:hidden"> ile kapat</span>
            </p>
          </div>
        </div>

        <!-- Spotlight ring effect - makes target stand out -->
        <div
          v-if="targetRect"
          class="absolute transition-all duration-500 ease-out pointer-events-none"
          :style="spotlightStyle"
        >
          <div class="absolute inset-0 rounded-xl ring-2 ring-blue-400/70 shadow-2xl"></div>
        </div>

        <!-- Tooltip -->
        <div
          v-if="targetRect"
          class="absolute pointer-events-auto transition-all duration-500 ease-out w-[280px] sm:w-[320px] md:max-w-sm"
          :style="tooltipStyle"
        >
          <div class="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden w-full">
            <!-- Progress bar -->
            <div class="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" :style="{ width: progressWidth }"></div>

            <!-- Content -->
            <div class="p-4 sm:p-5">
              <div class="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                <h3 class="text-sm sm:text-base font-bold text-gray-900 leading-tight">{{ currentStep?.title }}</h3>
                <button
                  @click="skip"
                  class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Turu atla"
                >
                  <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <p class="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3 sm:mb-4">{{ currentStep?.description }}</p>

              <!-- Navigation -->
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1 sm:gap-1.5">
                  <button
                    v-for="(_step, index) in steps"
                    :key="index"
                    @click="goToStep(index)"
                    class="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300"
                    :class="index === currentStepIndex ? 'bg-indigo-500 w-4 sm:w-6' : 'bg-gray-300 hover:bg-gray-400'"
                  ></button>
                </div>

                <div class="flex items-center gap-1.5 sm:gap-2">
                  <button
                    v-if="currentStepIndex > 0"
                    @click="prev"
                    class="px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Geri
                  </button>
                  <button
                    @click="next"
                    class="px-3 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
                  >
                    {{ isLastStep ? 'Tamamla' : 'İleri' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Arrow -->
          <div
            class="absolute w-3 h-3 bg-white border-l border-t border-gray-100 transform rotate-45"
            :style="arrowStyle"
          ></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

export interface TourStep {
  target: string; // CSS selector
  title: string;
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

interface Props {
  steps: TourStep[];
  modelValue: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  complete: [];
  skip: [];
}>();

const currentStepIndex = ref(0);
const targetRect = ref<DOMRect | null>(null);
const isVisible = computed(() => props.modelValue);

const currentStep = computed(() => props.steps[currentStepIndex.value]);
const isLastStep = computed(() => currentStepIndex.value === props.steps.length - 1);
const progressWidth = computed(() => `${((currentStepIndex.value + 1) / props.steps.length) * 100}%`);

const scrollToTarget = (element: Element) => {
  const rect = element.getBoundingClientRect();
  const isInView = (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );

  // If element is not in view, scroll to it
  if (!isInView) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    });
  }
};

const updateTargetRect = async (shouldScroll = true) => {
  await nextTick();
  if (!currentStep.value) return;

  // Try to find element with retries (max 3 attempts)
  let attempts = 0;
  const maxAttempts = 3;

  const findElement = async (): Promise<Element | null> => {
    const element = document.querySelector(currentStep.value.target);
    if (element) return element;

    if (attempts < maxAttempts) {
      attempts++;
      await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
      return findElement();
    }

    return null;
  };

  const element = await findElement();

  if (element) {
    // Scroll to element if it's not in view (only on initial show, not on scroll updates)
    if (shouldScroll) {
      scrollToTarget(element);
      // Wait for scroll to finish before getting rect
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    targetRect.value = element.getBoundingClientRect();
  } else {
    // Element not found after retries - skip this step or close tour
    console.warn(`Tour target not found: ${currentStep.value.target}`);
    // Auto-skip to next step or close if last step
    if (isLastStep.value) {
      skip();
    } else {
      next();
    }
  }
};

const spotlightStyle = computed(() => {
  if (!targetRect.value) return {};
  return {
    left: `${targetRect.value.left - 8}px`,
    top: `${targetRect.value.top - 8}px`,
    width: `${targetRect.value.width + 16}px`,
    height: `${targetRect.value.height + 16}px`,
  };
});

const tooltipStyle = computed<Record<string, string>>(() => {
  if (!targetRect.value) return {};

  const isMobile = window.innerWidth < 768;
  const offset = isMobile ? 10 : 20;
  const tooltipWidth = isMobile ? 280 : 384; // 280px mobile, 384px desktop (max-w-sm)
  const tooltipHeight = 250; // Approximate tooltip height
  const padding = 20;

  // Auto-adjust placement for mobile - always use bottom
  let placement = currentStep.value?.placement || 'right';
  if (isMobile) {
    placement = 'bottom';
  }

  // Helper function to calculate placement without recursion
  const calculatePlacement = (currentPlacement: string): Record<string, string> => {
    switch (currentPlacement) {
      case 'right': {
        // Check if tooltip would go off right edge
        const wouldOverflowRight = targetRect.value!.right + offset + tooltipWidth > window.innerWidth;

        if (wouldOverflowRight) {
          // Switch to left placement
          return calculatePlacement('left');
        }

        // Check if tooltip would go off bottom of screen
        let topPosition = targetRect.value!.top;

        // If tooltip would extend past bottom of viewport, adjust it
        if (topPosition + tooltipHeight > window.innerHeight) {
          topPosition = window.innerHeight - tooltipHeight - padding;
        }

        // Ensure it doesn't go above viewport
        if (topPosition < padding) {
          topPosition = padding;
        }

        return {
          left: `${targetRect.value!.right + offset}px`,
          top: `${topPosition}px`,
          transform: 'translateY(0)',
        };
      }
      case 'left': {
        // Check if tooltip would go off left edge
        const wouldOverflowLeft = targetRect.value!.left - offset - tooltipWidth < 0;

        if (wouldOverflowLeft) {
          // Switch to right or bottom placement
          return calculatePlacement(isMobile ? 'bottom' : 'right');
        }

        let leftTopPosition = targetRect.value!.top;

        if (leftTopPosition + tooltipHeight > window.innerHeight) {
          leftTopPosition = window.innerHeight - tooltipHeight - padding;
        }

        if (leftTopPosition < padding) {
          leftTopPosition = padding;
        }

        return {
          right: `${window.innerWidth - targetRect.value!.left + offset}px`,
          top: `${leftTopPosition}px`,
          transform: 'translateY(0)',
        };
      }
      case 'bottom': {
        // Check if tooltip would go off bottom
        const wouldOverflowBottom = targetRect.value!.bottom + offset + tooltipHeight > window.innerHeight;

        if (wouldOverflowBottom) {
          // Switch to top placement
          return calculatePlacement('top');
        }

        // Center horizontally but ensure it doesn't go off screen
        let leftPosition = targetRect.value!.left + targetRect.value!.width / 2;

        // Check horizontal bounds
        if (leftPosition - tooltipWidth / 2 < padding) {
          leftPosition = tooltipWidth / 2 + padding;
        } else if (leftPosition + tooltipWidth / 2 > window.innerWidth - padding) {
          leftPosition = window.innerWidth - tooltipWidth / 2 - padding;
        }

        return {
          left: `${leftPosition}px`,
          top: `${targetRect.value!.bottom + offset}px`,
          transform: 'translateX(-50%)',
        };
      }
      case 'top': {
        // Check if tooltip would go off top
        const wouldOverflowTop = targetRect.value!.top - offset - tooltipHeight < 0;

        if (wouldOverflowTop) {
          // Switch to bottom placement
          return calculatePlacement('bottom');
        }

        // Center horizontally but ensure it doesn't go off screen
        let leftPosition = targetRect.value!.left + targetRect.value!.width / 2;

        // Check horizontal bounds
        if (leftPosition - tooltipWidth / 2 < padding) {
          leftPosition = tooltipWidth / 2 + padding;
        } else if (leftPosition + tooltipWidth / 2 > window.innerWidth - padding) {
          leftPosition = window.innerWidth - tooltipWidth / 2 - padding;
        }

        return {
          left: `${leftPosition}px`,
          bottom: `${window.innerHeight - targetRect.value!.top + offset}px`,
          transform: 'translateX(-50%)',
        };
      }
      default:
        return {};
    }
  };

  return calculatePlacement(placement);
});

const arrowStyle = computed(() => {
  if (!targetRect.value) return {};

  const placement = currentStep.value?.placement || 'right';
  const tooltipHeight = 250;

  switch (placement) {
    case 'right':
    case 'left': {
      // Calculate where the arrow should point to the target
      let topPosition = targetRect.value.top;

      // If tooltip was adjusted, calculate arrow position relative to tooltip
      if (topPosition + tooltipHeight > window.innerHeight) {
        topPosition = window.innerHeight - tooltipHeight - 20;
      }
      if (topPosition < 20) {
        topPosition = 20;
      }

      // Arrow should point to the middle of the target element
      const arrowTop = targetRect.value.top + (targetRect.value.height / 2) - topPosition;

      return placement === 'right'
        ? { left: '-6px', top: `${arrowTop}px`, transform: 'rotate(45deg)' }
        : { right: '-6px', top: `${arrowTop}px`, transform: 'rotate(225deg)' };
    }
    case 'bottom':
      return { left: '50%', top: '-6px', transform: 'translateX(-50%) rotate(45deg)' };
    case 'top':
      return { left: '50%', bottom: '-6px', transform: 'translateX(-50%) rotate(225deg)' };
    default:
      return {};
  }
});

const next = () => {
  if (isLastStep.value) {
    complete();
  } else {
    currentStepIndex.value++;
    updateTargetRect();
  }
};

const prev = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
    updateTargetRect();
  }
};

const goToStep = (index: number) => {
  currentStepIndex.value = index;
  updateTargetRect();
};

const skip = () => {
  emit('skip');
  emit('update:modelValue', false);
};

const complete = () => {
  emit('complete');
  emit('update:modelValue', false);
};

const handleOverlayClick = () => {
  // Close tour when clicking on the dark overlay
  skip();
};

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isVisible.value) {
    skip();
  }
};

// Handle scroll events - update position without auto-scrolling
const handleScroll = () => {
  if (isVisible.value) {
    updateTargetRect(false); // false = don't auto-scroll, just update position
  }
};

// Throttle scroll events for performance
let scrollTimeout: number | null = null;
const throttledScroll = () => {
  if (scrollTimeout) return;
  scrollTimeout = window.setTimeout(() => {
    handleScroll();
    scrollTimeout = null;
  }, 16); // ~60fps
};

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    currentStepIndex.value = 0;
    updateTargetRect(true); // true = auto-scroll to target
  }
});

watch(currentStepIndex, () => {
  updateTargetRect(true); // true = auto-scroll to target when step changes
});

onMounted(() => {
  window.addEventListener('resize', () => updateTargetRect(false));
  window.addEventListener('scroll', throttledScroll, true); // true = capture phase (catches all scroll events)
  window.addEventListener('keydown', handleEscapeKey);
  if (isVisible.value) {
    updateTargetRect(true);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', () => updateTargetRect(false));
  window.removeEventListener('scroll', throttledScroll, true);
  window.removeEventListener('keydown', handleEscapeKey);
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
});
</script>

<style scoped>
.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity 0.3s ease;
}

.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}
</style>
